


import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiClock, FiUser, FiArrowLeft } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api.js';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await api.get(`/api/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-20 section-padding bg-dark-900">
        <div className="container-custom text-center">
          <h1 className="text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100">Post Not Found</h1>
          <p className="text-xl mb-8 text-gray-600 dark:text-gray-300">
            The blog post you're looking for doesn't exist.
          </p>
          <Link to="/blog" className="btn-primary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const backendUrl = import.meta.env.VITE_API_URL;
  const imageUrl = post.image
    ? post.image.startsWith('http')
      ? post.image
      : `${backendUrl}${post.image}`
    : 'https://via.placeholder.com/800x450';

  return (
    <>
      <Helmet>
        <title>{post.title} - Portfolio Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="pt-20 bg-dark-900 min-h-screen">
        <article className="max-w-4xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center text-primary-500 hover:underline mb-6"
            >
              <FiArrowLeft className="mr-2" />
              Back to Blog
            </Link>

            <div className="aspect-video mb-8 rounded-lg overflow-hidden">
              <img
                src={imageUrl}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-4 mb-4 text-sm flex-wrap text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <FiCalendar size={14} />
                  <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FiClock size={14} />
                  <span>{post.readTime || '5 min read'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FiUser size={14} />
                  <span>{post.author || 'Admin'}</span>
                </div>
              </div>

              <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-gray-100">
                {post.title}
              </h1>

              <p className="text-xl mb-6 text-gray-600 dark:text-gray-300">
                {post.excerpt}
              </p>

              <div className="flex flex-wrap gap-2">
                {post.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-sm rounded-full bg-dark-700 text-gray-600 dark:text-gray-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </motion.div>
        </article>
      </div>
    </>
  );
};

export default BlogPost;